/**
 * Header-slot cluster for the editor's comment controls.
 *
 * Renders the unresolved-count pill and the Open/Resolved/All
 * segmented filter (#2246) so both live next to the title. The filter
 * reads/writes `CommentFilterContext`, which every mounted
 * `CommentsProvider` (source + translation) consumes to scope its
 * inline markers.
 *
 * Comments themselves render as anchored flyout cards in
 * `CommentsGutter` (#2244). Paragraph leaves still render an
 * amber/emerald underline + icon as a discovery hint — clicking an
 * icon scrolls the matching flyout card into view via a window
 * event. See `src/components/editors/shared/comments/comments-gutter.tsx`.
 */

import { useTranslation } from 'react-i18next';

import { useGetComments } from '@utils/api/comments/use-get-comments.tsx';
import { SegmentedControl } from '@components/atoms/ui-kit/segmented-control.tsx';
import {
  CommentFilter,
  useCommentFilter
} from '@components/editors/shared/comments/comments-filter-context.tsx';
import type { Comment } from '@utils/types/comments/comments.ts';

function countUnresolved(comments: Comment[]): number {
  return comments.filter(c => !c.resolved).length;
}

function countResolved(comments: Comment[]): number {
  return comments.filter(c => !!c.resolved).length;
}

interface CommentsHeaderControlsProps {
  sessionId: string;
  documentId: string;
  /**
   * Optional secondary document whose comments should contribute to
   * the header's open/resolved tallies. Set to the translation doc id
   * when split view is live so counts reflect comments on either pane
   * — the header sits above both `CommentsProvider`s and the user
   * expects "N resolved" to be the total they can see.
   */
  translationDocumentId?: string | null;
}

export function CommentsHeaderControls({
  sessionId,
  documentId,
  translationDocumentId
}: CommentsHeaderControlsProps) {
  const { t } = useTranslation();
  const { filter, setFilter } = useCommentFilter();
  const { data } = useGetComments({
    session_id: sessionId,
    document_id: documentId
  });
  // Request the second slice unconditionally so hook order is stable;
  // when there is no translation pane we point the second query at the
  // same doc id as the first. React-query de-duplicates by key, so the
  // extra useGetComments call is free in that case.
  const secondaryId = translationDocumentId || documentId;
  const { data: translationData } = useGetComments({
    session_id: sessionId,
    document_id: secondaryId
  });
  const comments = data?.result ?? [];
  const extra =
    secondaryId !== documentId ? (translationData?.result ?? []) : [];
  const openCount = countUnresolved(comments) + countUnresolved(extra);
  const resolvedCount = countResolved(comments) + countResolved(extra);

  // Nothing to show when the document has no comments at all — collapse
  // the slot rather than parking an empty filter next to the title.
  if (openCount === 0 && resolvedCount === 0) return null;

  return (
    <div className="flex items-center gap-2">
      {/* The standalone "N open" pill used to live here. It duplicated
          the `Open (N)` segment's count on its own, so the header read
          "4 open · Open (4) | Resolved (3) | All". The SegmentedControl
          already carries the count on each segment. */}
      <SegmentedControl<CommentFilter>
        ariaLabel={t('comment-filter')}
        value={filter}
        onChange={setFilter}
        options={[
          {
            value: 'open',
            label: t('comment-filter-open'),
            count: openCount
          },
          {
            value: 'resolved',
            label: t('comment-filter-resolved'),
            count: resolvedCount
          },
          {
            value: 'all',
            label: t('comment-filter-all')
          }
        ]}
      />
    </div>
  );
}

/**
 * Backwards-compatible export for the old count-only pill. New callers
 * should use `CommentsHeaderControls`. Kept as an alias so any code
 * still importing this by name compiles without touching that call
 * site — safe because the new cluster contains the same pill.
 */
export const UnresolvedCommentCount = CommentsHeaderControls;
